import { useMemo, useCallback, useState, useRef, useEffect } from 'react';
import { Nav, Loader } from 'rsuite';
import { IoPersonCircle, IoClose } from 'react-icons/io5';
import { useImpersonation } from '../../contexts/ImpersonationContext';
import SelectPicker from '../select-picker';
import './impersonation-picker.css';

function ImpersonationPicker({ expanded }) {
    const {
        impersonatedUser,
        employees,
        isLoadingEmployees,
        setImpersonatedUser,
        clearImpersonation,
        refreshEmployees,
        isAdmin,
    } = useImpersonation();

    const [open, setOpen] = useState(false);
    const wrapperRef = useRef(null);

    const pickerData = useMemo(() =>
        employees.map((emp) => ({
            label: emp.name,
            value: emp.email,
            role: emp.role,
            centres: (emp.costCentreNames ?? []).join(', '),
        })),
    [employees]);

    const handleChange = useCallback((selected) => {
        if (!selected) {
            clearImpersonation();
            return;
        }
        const employee = employees.find((emp) => emp.email === selected.value);
        if (employee) {
            setImpersonatedUser(employee);
            setOpen(false);
        }
    }, [employees, setImpersonatedUser, clearImpersonation]);

    const filterOption = useCallback((option, rawInput) => {
        if (!rawInput) return true;
        const words = rawInput.split(' ').map((w) => w.toLowerCase());
        const label = (option.data.label ?? '').toLowerCase();
        const centres = (option.data.centres ?? '').toLowerCase();
        const role = (option.data.role ?? '').toLowerCase();
        return words.every((w) => label.includes(w) || centres.includes(w) || role.includes(w));
    }, []);

    const getOptionLabel = useCallback((option) => {
        if (!option) return null;
        return (
            <span className="impersonation-option">
                <span className="impersonation-option-name">{option.label}</span>
                {option.role && (
                    <span className="impersonation-option-role">{option.role}</span>
                )}
                {option.centres && (
                    <span className="impersonation-option-centres">{option.centres}</span>
                )}
            </span>
        );
    }, []);

    const handleClickOutside = useCallback((event) => {
        if (event.target.closest('[class*="select-picker__"]')) return;
        if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
            setOpen(false);
        }
    }, []);

    useEffect(() => {
        if (!open) return undefined;
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [open, handleClickOutside]);

    if (!isAdmin) {
        return null;
    }

    const buttonLabel = impersonatedUser ? impersonatedUser.name : 'Run as';

    return (
        <div
            className={`impersonation-picker-wrapper${expanded ? ' expanded' : ''}`}
            ref={wrapperRef}>
            <Nav>
                <Nav.Item
                    data-testid="impersonation-picker-button"
                    onClick={() => setOpen((prev) => {
                        const next = !prev;
                        if (next) refreshEmployees();
                        return next;
                    })}
                    icon={
                        <IoPersonCircle
                            className={`rs-icon impersonation-icon${impersonatedUser ? ' impersonation-icon-active' : ''}`}
                        />
                    }
                    style={{ userSelect: 'none' }}>
                    {buttonLabel}
                </Nav.Item>
            </Nav>

            {open && (
                <div className="impersonation-panel" role="dialog" aria-label="Run as another user">
                    <div className="impersonation-panel-header">
                        <span className="impersonation-panel-title">Run as another user</span>
                        <button
                            type="button"
                            className="impersonation-panel-close"
                            onClick={() => setOpen(false)}
                            aria-label="Close run-as panel">
                            <IoClose />
                        </button>
                    </div>

                    {impersonatedUser && (
                        <div className="impersonation-panel-current">
                            <span className="impersonation-panel-current-label">Currently:</span>
                            <span className="impersonation-panel-current-name">{impersonatedUser.name}</span>
                            <button
                                type="button"
                                className="impersonation-panel-clear"
                                onClick={() => {
                                    clearImpersonation();
                                    setOpen(false);
                                }}>
                                Stop
                            </button>
                        </div>
                    )}

                    {isLoadingEmployees ? (
                        <div className="impersonation-panel-loading">
                            <Loader size="sm" content="Loading users..." />
                        </div>
                    ) : (
                        <SelectPicker
                            data={pickerData}
                            value={impersonatedUser?.email ?? null}
                            onChange={handleChange}
                            placeholder="Search users..."
                            className="impersonation-select"
                            testid="impersonation-picker"
                            filterOption={filterOption}
                            getOptionLabel={getOptionLabel}
                            isClearable
                            menuPlacement="auto"
                            defaultMenuIsOpen
                        />
                    )}
                </div>
            )}
        </div>
    );
}

export default ImpersonationPicker;
